/**
 * de-DE display formatting for the Deckungsbeitrag app (#1781). All money
 * is EUR (the fact contract pins ``currency: "EUR"``); unknown nullables
 * render an em dash — never NaN, never a fabricated 0.
 */

import { BASIS_META, type MarginBasis } from './basis';
import { normaliseYieldUnit, type KnownYieldUnit } from './yieldUnit';

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const HA = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

export const formatEur = (value: number): string => EUR.format(value);

export const formatEurOrDash = (value: number | null): string => (value == null ? '—' : EUR.format(value));

export const formatHaOrDash = (value: number | null): string => (value == null ? '—' : HA.format(value));

/** Plain de-DE number (max 2 fraction digits) — quantities, Standjahre, plant counts (#2508). */
export const formatNumber = (value: number): string => HA.format(value);

/** €/{basis unit}, e.g. "1.234,00 €/ha" / "…/kg" / "…/lfm" / "…/Pflanze" (#2304); the bare euro for € gesamt. */
export const formatEurPerBasis = (value: number, basis: MarginBasis): string => {
  const { unit } = BASIS_META[basis];
  return unit ? `${EUR.format(value)}/${unit}` : EUR.format(value);
};

/** Back-compat alias — the ``ha`` special case of ``formatEurPerBasis``. */
export const formatEurPerHa = (value: number): string => formatEurPerBasis(value, 'ha');

// #2168 \u2014 unit-aware yield formatting. Arable harvests are dt (farmers think \u20ac/dt, dt/ha);
// everything else (the berry pick stream, and pre-#2168 facts with a null unit) stays kg.
//
// #2695 E WIDENS the pass-through from the single "dt" literal to every unit the worker can
// actually put on a fact, because the old form was a silent relabel: the sale-price converter
// (worker _DT_PER_YIELD_UNIT) accepts dt, t AND kg, and it CASEFOLDS, so a fact carrying "t"
// rendered "306 kg" for 306 tonnes and a fact carrying "Dt" rendered "306 kg" for 306 dt. That
// was survivable while nothing else on screen disagreed; it stops being survivable next to the
// #2695 estimate note, which prints the unit VERBATIM one line away: the same panel would show
// "Ertrag 30,6 kg" directly above "30,6 t x 190,00 EUR/t". An unrecognised unit still degrades to
// kg (never to a unit this app cannot name), so no existing behaviour changes for dt/kg/null.
//
// #2930 moves the unit SET out of this module into ``yieldUnit.ts``, because widening the label
// side alone left the SCALER (``yieldInKg``, the je-kg ranking denominator) still matching one
// case-sensitive literal 'dt' — so this app could NAME three units and SCALE exactly one, and a
// tonne fact was ranked 1000x too high under a column headed "je kg" while this helper headed
// the identical number "Je Tonne". Both sides now read the same ``KG_PER_YIELD_UNIT`` key set,
// so the label range and the scale range ARE one object's keys and cannot drift apart again.
// Behaviour here is unchanged for every input, including the degrade.

/** The yield unit as DECLARED where this app can name it, else "kg". */
export const yieldUnitLabel = (unit: string | null): KnownYieldUnit => normaliseYieldUnit(unit) ?? 'kg';

const unitLabel = yieldUnitLabel;

export const formatEurPerUnit = (value: number, unit: string | null): string =>
  `${EUR.format(value)}/${unitLabel(unit)}`;

export const formatQty = (value: number, unit: string | null): string =>
  `${HA.format(value)}\u00a0${unitLabel(unit)}`;

// #2695 E: the two formatters for a unit that came off the FACT rather than out of this app.
//
// WARNING - the pair above must NOT be used for those. ``unitLabel`` collapses everything
// OUTSIDE ``KG_PER_YIELD_UNIT`` to "kg" (this said "everything that is not dt" until #2695 E
// widened it and #2930 moved the key set out; the conclusion below is unchanged, because a
// price unit arrives as the whole "EUR/t" STRING, which is not a key of that table either).
// That is safe for the #2168 yield axis (dt, t or kg by construction) and
// WRONG the moment a unit arrives from the sale price book: the book prices arable in EUR/t,
// Kartoffeln in EUR/dt and berries in EUR/kg, so a EUR/t price routed through
// ``formatEurPerUnit`` prints "EUR/kg" - a 1000x misread of the exact number #2695 exists to
// make trustworthy, in the favourable direction. The two below print what the fact says and
// interpret nothing.

/**
 * #2695 E: a price in ITS OWN declared unit. ``priceUnit`` is the full "EUR/..." string off
 * the fact (EUR/t | EUR/dt | EUR/kg, written with the currency SYMBOL on the fact itself), so
 * the currency formatting applies to the number while the DENOMINATOR is taken verbatim:
 * ``formatBookPrice(190, "<sym>/t")`` gives ``"190,00 <sym>/t"``.
 * A unit in any other shape is appended whole rather than being reinterpreted.
 */
export const formatBookPrice = (value: number, priceUnit: string): string => {
  const denominator = priceUnit.split('/')[1]?.trim();
  return denominator ? `${EUR.format(value)}/${denominator}` : `${EUR.format(value)} ${priceUnit}`;
};

/** #2695 E: a quantity in ITS OWN declared unit, verbatim (``306`` + ``"dt"`` gives ``"306 dt"``). */
export const formatBookQty = (value: number, unit: string): string => `${HA.format(value)}\u00a0${unit}`;

/**
 * A ``YYYY-MM`` month as a farmer reads it \u2014 \u201eJuni 2026". The Leistung Einzelposten are
 * folded to months (``BreakdownItem.month``), so the Datum column names the month rather
 * than a pick day. A malformed key prints verbatim rather than as \u201eInvalid Date".
 */
export const formatMonth = (yearMonth: string): string => {
  const d = new Date(`${yearMonth}-01T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? yearMonth
    : d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

/**
 * HOF-467 follow-up — the unit a Posten's Menge is printed in: a yield unit this app can name
 * is normalised (``Dt`` → ``dt``, exactly as ``formatQty``), any OTHER unit the booking declares
 * (``l``, ``Stk``) is kept verbatim, and only an ABSENT unit falls back to kg (a pre-#2168 pick).
 * ``formatQty``'s kg-collapse is right for the yield axis, whose range is dt/t/kg by
 * construction; it is wrong for a material input, where a litre of Pflanzenschutz printed as
 * "kg" is the #2695 D relabelling in a new coat.
 */
export const postenUnitLabel = (unit: string | null | undefined): string =>
  unit == null || unit.trim() === '' ? 'kg' : (normaliseYieldUnit(unit) ?? unit.trim());

/** A Posten's Menge with its own unit — ``2,93 l``, ``500 kg``, ``306 dt``. */
export const formatPostenQty = (value: number, unit: string | null | undefined): string =>
  `${HA.format(value)}\u00a0${postenUnitLabel(unit)}`;
